import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import { getBeautySalonsApi, getState } from "../../api/api";
import useCitiesByState from "../../hooks/useCitiesByState";
import RIcon from "../reservas/components/ReservasIcons";
import { SucursalesChip } from "../reservas/components/Sucursales";
import {
	SingleDatePickerPill,
	PillDropdown,
	MultiPillDropdown,
	BEAUTY_FALLBACK_IMAGE,
	ReservationKindTabs,
	formatPillDateLabel,
	resolveUrl,
} from "../reservas/components/ReservaShared";
import OrganizerTimePillSelect from "../../organizer/components/OrganizerTimePillSelect";
import { formatMoney } from "./beautyBooking";
import { RatingBadge } from "../reservas/components/RatingStars";
import { RATING_RANGE_OPTIONS, ratingRangeLabel, ratingRangesParam } from "../reservas/components/reviewsModel";
import "../reservas/reservas.css";
import "./beauty.css";

const normalizeSalons = (response) => {
	const info = response?.data?.info;
	return Array.isArray(info) ? info : [];
};

// Multi-select: an empty selection (or both options) means "all".
const DEPOSIT_OPTIONS = [
	{ value: "deposit", label: "Con seña online" },
	{ value: "free", label: "Sin seña" },
];

const NO_SEARCH = { date: "", time: "" };

const categoriesOf = (salon) =>
	Array.isArray(salon?.serviceCategories) ? salon.serviceCategories : [];

const BeautyListing = () => {
	const navigate = useNavigate();

	const [salons, setSalons] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const latestRequestId = useRef(0);

	const [nameFilter, setNameFilter] = useState("");
	const [stateId, setStateId] = useState("");
	const [cityId, setCityId] = useState("");
	const [category, setCategory] = useState("");
	// Client-side live filter: substring over the salon's active service names (`serviceNames`).
	const [serviceFilter, setServiceFilter] = useState("");
	// Client-side filter on the list payload's reservationMode: subset of ["deposit", "free"].
	const [depositModes, setDepositModes] = useState([]);
	// Server-side filter: subset of ["4-5", "3-4", "0-3"] (sent as the CSV `ratingRanges`).
	const [ratingRanges, setRatingRanges] = useState([]);
	const [showSecondary, setShowSecondary] = useState(false);
	// Chip list: union of the categories of the last fetch made without a
	// category, so picking a chip does not make the other chips disappear.
	const [allCategories, setAllCategories] = useState([]);
	// Display-only default (tomorrow), like Canchas: `date` is only sent after an
	// explicit "Buscar", never on mount or on automatic province/city reloads.
	const [selectedDate, setSelectedDate] = useState(() => dayjs().add(1, "day").startOf("day"));
	const [timeFilter, setTimeFilter] = useState("");
	// The date/time of the last explicit "Buscar" (empty = no availability search
	// active). Later reloads (province, city, category) keep sending it, so the
	// list never disagrees with the chips on screen.
	const [applied, setApplied] = useState(NO_SEARCH);
	const hasExplicitSearched = Boolean(applied.date);

	const [states, setStates] = useState([]);
	const { cities } = useCitiesByState(stateId);

	const minDate = dayjs().startOf("day");
	const maxDate = minDate.add(60, "day");

	useEffect(() => {
		let cancelled = false;
		getState()
			.then((res) => {
				if (!cancelled) setStates(Array.isArray(res?.data?.info) ? res.data.info : []);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	const loadSalons = useCallback(async ({ st, ct, cat, rating = [], date = "", time = "" }) => {
		const requestId = ++latestRequestId.current;
		try {
			setLoading(true);
			setError("");
			const params = {};
			if (st) params.stateid = st;
			if (ct) params.cityid = ct;
			if (cat) params.category = cat;
			if (rating.length) params.ratingRanges = ratingRangesParam(rating);
			if (date) {
				params.date = date;
				if (time) params.time = time;
			}
			const response = await getBeautySalonsApi(params);
			if (requestId !== latestRequestId.current) return;
			const list = normalizeSalons(response);
			setSalons(list);
			// The chips list comes from the unfiltered fetch only: a date search can
			// hide categories that exist.
			if (!cat && !date && !rating.length) {
				const union = Array.from(new Set(list.flatMap(categoriesOf))).sort((a, b) =>
					String(a).localeCompare(String(b), "es", { sensitivity: "base" }),
				);
				setAllCategories(union);
			}
		} catch (err) {
			if (requestId === latestRequestId.current) {
				setError(err?.response?.data?.message || "No pudimos cargar los locales.");
			}
		} finally {
			if (requestId === latestRequestId.current) setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadSalons({ st: "", ct: "", cat: "" });
	}, [loadSalons]);

	const handleStateChange = (val) => {
		setStateId(val);
		setCityId("");
		loadSalons({ st: val, ct: "", cat: category, rating: ratingRanges, ...applied });
	};

	const handleCityChange = (val) => {
		setCityId(val);
		loadSalons({ st: stateId, ct: val, cat: category, rating: ratingRanges, ...applied });
	};

	const handleCategory = (val) => {
		setCategory(val);
		loadSalons({ st: stateId, ct: cityId, cat: val, rating: ratingRanges, ...applied });
	};

	const handleRating = (val) => {
		setRatingRanges(val);
		loadSalons({ st: stateId, ct: cityId, cat: category, rating: val, ...applied });
	};

	const handleRetry = () => loadSalons({ st: stateId, ct: cityId, cat: category, rating: ratingRanges, ...applied });

	// Explicit search: the only place where the picked date (and time) are sent.
	const handleSearch = () => {
		const date = selectedDate ? selectedDate.format("YYYY-MM-DD") : "";
		const next = date ? { date, time: timeFilter } : NO_SEARCH;
		setApplied(next);
		loadSalons({ st: stateId, ct: cityId, cat: category, rating: ratingRanges, ...next });
	};

	const clearAppliedDate = () => {
		setSelectedDate(null);
		setTimeFilter("");
		setApplied(NO_SEARCH);
		loadSalons({ st: stateId, ct: cityId, cat: category, rating: ratingRanges });
	};

	const clearAppliedTime = () => {
		setTimeFilter("");
		const next = { ...applied, time: "" };
		setApplied(next);
		loadSalons({ st: stateId, ct: cityId, cat: category, rating: ratingRanges, ...next });
	};

	const secondaryCount = [category, depositModes.length, ratingRanges.length].filter(Boolean).length;

	const hasFilters = Boolean(
		nameFilter.trim() ||
			serviceFilter.trim() ||
			stateId ||
			cityId ||
			category ||
			depositModes.length ||
			ratingRanges.length ||
			applied.date,
	);
	const handleClear = () => {
		setNameFilter("");
		setStateId("");
		setCityId("");
		setCategory("");
		setServiceFilter("");
		setDepositModes([]);
		setRatingRanges([]);
		setSelectedDate(dayjs().add(1, "day").startOf("day"));
		setTimeFilter("");
		setApplied(NO_SEARCH);
		loadSalons({ st: "", ct: "", cat: "" });
	};

	// Slot pill: open the salon with the searched date so the booking flow
	// preselects it (services come first, so the slot itself is not preselected).
	const handleSlotClick = (salon, slot) => {
		const salonRef = salon.slug || salon._id;
		navigate(`/beauty/${salonRef}`, { state: { date: applied.date, time: slot?.slotStart || applied.time } });
	};

	const filteredSalons = useMemo(() => {
		const q = nameFilter.trim().toLowerCase();
		const sq = serviceFilter.trim().toLowerCase();
		return salons.filter((s) => {
			if (q && !(s.name || "").toLowerCase().includes(q)) return false;
			if (sq) {
				const names = Array.isArray(s.serviceNames) ? s.serviceNames : [];
				if (!names.some((n) => String(n || "").toLowerCase().includes(sq))) return false;
			}
			// Both (or none) selected = no deposit filter; a single one narrows the list.
			if (depositModes.length === 1) {
				const isDeposit = s.reservationMode === "deposit";
				return depositModes[0] === "deposit" ? isDeposit : !isDeposit;
			}
			return true;
		});
	}, [salons, nameFilter, serviceFilter, depositModes]);

	const categoryOptions = [
		{ value: "", label: "Todas" },
		...allCategories.map((c) => ({ value: c, label: c })),
	];

	return (
		<div className="reservas-root">
			<div className="wrap">
				<ReservationKindTabs active="beauty" />

				<div className="res-head">
					<div className="res-actions">
						<div>
							<div className="eyebrow">Beauty</div>
							<h1>Reservá tu turno</h1>
						</div>
					</div>
					<p className="lead">
						Encontrá tu peluquería, barbería o centro de estética y reservá en segundos.
					</p>
				</div>

				<div className="res-filters-container">
					<div className="res-filters canchas-filters-main beauty-filters-main">

						<div className="rf rf-name">
							<label htmlFor="beauty-service-filter">Nombre del servicio a realizar</label>
							<div className="rf-control">
								<RIcon name="search" />
								<input
									id="beauty-service-filter"
									placeholder="Opcional"
									value={serviceFilter}
									onChange={(e) => setServiceFilter(e.target.value)}
								/>
							</div>
						</div>

						<div className="rf rf-name">
							<label htmlFor="beauty-name-filter">Nombre del local</label>
							<div className="rf-control">
								<RIcon name="search" />
								<input
									id="beauty-name-filter"
									placeholder="Opcional"
									value={nameFilter}
									onChange={(e) => setNameFilter(e.target.value)}
								/>
							</div>
						</div>

						<div className="rf">
							<label>Provincia</label>
							<PillDropdown
								iconName="map"
								value={stateId}
								options={[
									{ value: "", label: "Todas" },
									...states.map((s) => ({ value: s._id, label: s.name })),
								]}
								onChange={handleStateChange}
								placeholder="Todas"
								ariaLabel="Provincia"
							/>
						</div>

						<div className="rf">
							<label>Ciudad</label>
							<PillDropdown
								iconName="pin"
								value={cityId}
								options={[
									{ value: "", label: "Todas" },
									...cities.map((c) => ({ value: c._id, label: c.name })),
								]}
								onChange={handleCityChange}
								placeholder="Todas"
								searchable={true}
								disabled={!stateId}
								ariaLabel="Ciudad"
							/>
						</div>

						<div className="rf">
							<label>Fecha</label>
							<SingleDatePickerPill
								value={selectedDate}
								onChange={setSelectedDate}
								minDate={minDate}
								maxDate={maxDate}
								ariaLabel="Fecha"
							/>
						</div>

						<div className="rf">
							<label>Horario</label>
							<OrganizerTimePillSelect
								value={timeFilter}
								onChange={(t) => setTimeFilter(t)}
								placeholder="--:--"
								triggerClassName="ef-date-pill-trigger"
								ariaLabel="Horario"
								minutes={["00", "30"]}
								singleList={true}
							/>
						</div>

						<div className="rf rf-search rf-double-btn">
							<button type="button" className="r-btn-primary" onClick={handleSearch}>
								<RIcon name="search" width={17} height={17} /> Buscar turnos
							</button>
							<button
								className="r-btn-soft"
								type="button"
								aria-expanded={showSecondary}
								onClick={() => setShowSecondary(!showSecondary)}
							>
								<RIcon name="filter" width={15} height={15} />
								{showSecondary ? "Menos filtros" : "Más filtros"}
								{secondaryCount > 0 && ` (${secondaryCount})`}
							</button>
						</div>
					</div>

					{showSecondary && (
						<div className="res-filters canchas-filters-secondary beauty-filters-secondary">
							{allCategories.length > 0 && (
								<div className="rf">
									<label>Categoría</label>
									<PillDropdown
										iconName="spark"
										value={category}
										options={categoryOptions}
										onChange={handleCategory}
										placeholder="Todas"
										ariaLabel="Categoría"
									/>
								</div>
							)}

							<div className="rf">
								<label>Seña</label>
								<MultiPillDropdown
									iconName="dollar"
									value={depositModes}
									options={DEPOSIT_OPTIONS}
									onChange={setDepositModes}
									placeholder="Todas"
									ariaLabel="Seña"
								/>
							</div>

							<div className="rf">
								<label>Valoración</label>
								<MultiPillDropdown
									iconName="star"
									value={ratingRanges}
									options={RATING_RANGE_OPTIONS}
									onChange={handleRating}
									placeholder="Todas"
									ariaLabel="Valoración"
								/>
							</div>
						</div>
					)}
				</div>

				<div className="res-meta">
					<div className="count">
						<b>{filteredSalons.length} {filteredSalons.length === 1 ? "local" : "locales"}</b>{" "}
						{hasExplicitSearched ? "con turnos disponibles" : "para reservar tu turno"}
					</div>
					{(category || depositModes.length > 0 || ratingRanges.length > 0 || applied.date) && (
						<div className="chips">
							{applied.date && (
								<span className="q-chip">
									<RIcon name="cal" width={13} height={13} />
									{formatPillDateLabel(dayjs(applied.date))}
									<button
										type="button"
										className="beauty-chip-clear"
										aria-label={`Quitar filtro ${formatPillDateLabel(dayjs(applied.date))}`}
										onClick={clearAppliedDate}
									>
										<RIcon name="x" width={10} height={10} />
									</button>
								</span>
							)}
							{applied.date && applied.time && (
								<span className="q-chip">
									<RIcon name="clock" width={13} height={13} />
									{`${applied.time} hs`}
									<button
										type="button"
										className="beauty-chip-clear"
										aria-label={`Quitar filtro ${applied.time} hs`}
										onClick={clearAppliedTime}
									>
										<RIcon name="x" width={10} height={10} />
									</button>
								</span>
							)}
							{category && (
								<span className="q-chip">
									<RIcon name="spark" width={13} height={13} />
									{category}
									<button
										type="button"
										className="beauty-chip-clear"
										aria-label={`Quitar filtro ${category}`}
										onClick={() => handleCategory("")}
									>
										<RIcon name="x" width={10} height={10} />
									</button>
								</span>
							)}
							{ratingRanges.map((range) => (
								<span className="q-chip" key={`rating-${range}`}>
									<RIcon name="star" width={13} height={13} />
									{`Valoración ${ratingRangeLabel(range)}`}
									<button
										type="button"
										className="beauty-chip-clear"
										aria-label={`Quitar filtro de valoración ${ratingRangeLabel(range)}`}
										onClick={() => handleRating(ratingRanges.filter((r) => r !== range))}
									>
										<RIcon name="x" width={10} height={10} />
									</button>
								</span>
							))}
							{depositModes.map((mode) => (
								<span className="q-chip" key={`deposit-${mode}`}>
									<RIcon name="dollar" width={13} height={13} />
									{DEPOSIT_OPTIONS.find((o) => o.value === mode)?.label}
									<button
										type="button"
										className="beauty-chip-clear"
										aria-label={`Quitar filtro ${DEPOSIT_OPTIONS.find((o) => o.value === mode)?.label}`}
										onClick={() => setDepositModes(depositModes.filter((m) => m !== mode))}
									>
										<RIcon name="x" width={10} height={10} />
									</button>
								</span>
							))}
						</div>
					)}
				</div>

				{error && (
					<Alert variant="danger">
						<div>{error}</div>
						<div className="beauty-error-actions mt-2">
							<button type="button" className="r-btn-soft" onClick={handleRetry}>
								Reintentar
							</button>
						</div>
					</Alert>
				)}

				{loading ? (
					<div style={{ textAlign: "center", padding: "60px 0" }}>
						<Spinner animation="border" />
					</div>
				) : error ? null : filteredSalons.length === 0 ? (
					<div className="empty-state">
						<RIcon name="spark" width={48} height={48} />
						<h3>No encontramos locales</h3>
						<p>Probá con otros filtros o volvé más tarde.</p>
						{hasFilters && (
							<button
								type="button"
								className="r-btn-primary"
								onClick={handleClear}
							>
								Limpiar filtros
							</button>
						)}
					</div>
				) : (
					<SalonsGrid
						list={filteredSalons}
						navigate={navigate}
						showSlots={hasExplicitSearched}
						onSlotClick={handleSlotClick}
					/>
				)}

				<div style={{ height: 60 }} />
			</div>
		</div>
	);
};

function SalonsGrid({ list, navigate, showSlots = false, onSlotClick }) {
	return (
		<div className="bars-grid">
			{list.map((salon) => {
				const salonRef = salon.slug || salon._id;
				const path = `/beauty/${salonRef}`;
				const img = resolveUrl(salon.image || salon.mobileimage) || BEAUTY_FALLBACK_IMAGE;
				const categories = categoriesOf(salon);
				const place = [salon.address, salon.cityid?.name].filter(Boolean).join(", ");
				// Pills only after an explicit date search AND when the back returned
				// nearbySlots (it does so only when a time was sent).
				const nearbySlots =
					showSlots && Array.isArray(salon.nearbySlots) ? salon.nearbySlots.slice(0, 4) : [];
				return (
					<div
						className="bar-card"
						key={String(salon._id)}
						data-testid="beauty-salon-card"
						onClick={() => navigate(path)}
					>
						<div className="bar-media">
							<img
								src={img}
								alt={salon.name || "Local"}
								onError={(e) => {
									e.currentTarget.src = BEAUTY_FALLBACK_IMAGE;
								}}
							/>
							{salon.priceFrom != null && (
								<div className="bar-media-deposit">
									<span className="deposit-label">Desde</span>
									<span className="deposit-val">{formatMoney(salon.priceFrom)}</span>
								</div>
							)}
						</div>
						<div className="bar-body">
							<div className="bar-name">{salon.name}</div>
							<SucursalesChip count={salon.sucursalesCount} />
							{Number(salon.ratingCount) > 0 && (
								<div className="rv-card-rating">
									<RatingBadge avg={salon.ratingAvg} count={salon.ratingCount} />
								</div>
							)}
							{place && (
								<div className="bar-loc">
									<RIcon name="pin" width={14} height={14} />
									{place}
								</div>
							)}
							{(categories.length > 0 || salon.reservationMode) && (
								<div className="bar-tags">
									{salon.reservationMode === "deposit" && (
										<span className="bar-tag">Con seña</span>
									)}
									{salon.reservationMode === "free" && (
										<span className="bar-tag">Sin seña</span>
									)}
									{categories.slice(0, 3).map((c) => (
										<span className="bar-tag" key={c}>
											{c}
										</span>
									))}
								</div>
							)}
							{nearbySlots.length > 0 && (
								<div className="bar-slots">
									{nearbySlots.map((slot) => (
										<button
											type="button"
											className="slot-btn"
											key={slot.slotStart}
											data-testid="beauty-slot-pill"
											onClick={(e) => {
												e.stopPropagation();
												onSlotClick?.(salon, slot);
											}}
										>
											{slot.slotStart}
										</button>
									))}
								</div>
							)}
							<div className="bar-foot centered">
								<a
									href={path}
									className="r-btn-primary bar-action-btn"
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										navigate(path);
									}}
								>
									Ver turnos
								</a>
							</div>
						</div>
					</div>
				);
			})}
		</div>
	);
}

export default BeautyListing;
